import { Component, OnInit, OnDestroy, HostListener, ChangeDetectorRef } from '@angular/core';
import { AgoraService } from 'src/app/core/services/agora.service';
import { AuthService } from 'src/app/core/services/auth.service';
import { OutgoingCallService } from 'src/app/core/services/outgoing-call.service';
import { RoutingService } from 'src/app/core/services/routing.service';
import { SocketService } from 'src/app/core/services/socket.service';
import { ChatDataService } from 'src/app/modules/components/chat/chat-data.service';

import { Observable, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

export type CallPhase = 'incoming' | 'active' | 'hold' | 'declined';
export type CallType = 'video' | 'audio';

function arcPos(angleDeg: number, R: number): { tx: number; ty: number } {
  const a = (angleDeg * Math.PI) / 180;
  return { tx: Math.round(R * Math.sin(a)), ty: Math.round(R * -Math.cos(a)) };
}

const START = 195, STEP = 38;
const ANGLES = [0, 1, 2, 3, 4].map(i => START + i * STEP);

const INCOMING_BTNS = [
  { id: 'in-decline', ...arcPos(ANGLES[0], 138), delay: 0, icon: 'call_end', label: 'Decline', color: '#ffb4ab', bg: '#93000a', pulseClass: 'pulse-red' },
  { id: 'in-audio', ...arcPos(ANGLES[1], 138), delay: 55, icon: 'mic', label: 'Audio', color: '#bbc3ff', bg: '#303f9f', pulseClass: '' },
  { id: 'in-hold', ...arcPos(ANGLES[2], 138), delay: 90, icon: 'pause_circle', label: 'Hold', color: '#44d8f1', bg: '#1a2a4a', pulseClass: 'pulse-blue' },
  { id: 'in-facetime', ...arcPos(ANGLES[3], 138), delay: 125, icon: 'videocam', label: 'Video', color: 'rgba(57,255,20,0.9)', bg: '#1a5c00', pulseClass: 'pulse-green' },
  { id: 'in-myroon', ...arcPos(ANGLES[4], 138), delay: 160, icon: 'spatial_audio_off', label: 'MyRoon', color: '#cebdff', bg: '#1a0a35', pulseClass: '' },
];

const ACTIVE_BTNS = [
  { id: 'ac-mute', ...arcPos(ANGLES[0], 155), delay: 0, icon: 'mic', iconOff: 'mic_off', label: 'Mute', labelOff: 'Unmute', color: '#c6c5d4', colorOn: '#ffb4ab', bg: '#222941', bgOn: 'rgba(147,0,10,.4)' },
  { id: 'ac-speaker', ...arcPos(ANGLES[1], 155), delay: 55, icon: 'videocam', iconOff: 'videocam_off', label: 'Video', labelOff: 'No Video', color: '#c6c5d4', colorOn: '#44d8f1', bg: '#222941', bgOn: 'rgba(68,216,241,.2)' },
  { id: 'ac-flip', ...arcPos(ANGLES[2], 155), delay: 90, icon: 'flip_camera_ios', iconOff: null, label: 'Flip', labelOff: null, color: '#c6c5d4', colorOn: '#c6c5d4', bg: '#222941', bgOn: '#222941' },
  { id: 'ac-chat', ...arcPos(ANGLES[3], 155), delay: 125, icon: 'chat', iconOff: null, label: 'Chat', labelOff: null, color: '#c6c5d4', colorOn: '#c6c5d4', bg: '#222941', bgOn: '#222941' },
  { id: 'ac-myroon', ...arcPos(ANGLES[4], 155), delay: 160, icon: 'spatial_audio_off', iconOff: null, label: 'MyRoon', labelOff: null, color: '#cebdff', colorOn: '#cebdff', bg: '#1a0a35', bgOn: '#2a1a45' },
];

const EQ_DELAYS = [0.1, 0.3, 0.2, 0.5, 0.4, 0.6, 0.2, 0.4, 0.7, 0.3, 0.1];
const EQ_HEIGHTS = [10, 22, 14, 30, 18, 26, 10, 18, 30, 14, 8];

@Component({
  selector: 'app-active-call',
  templateUrl: './active-call.component.html',
  styleUrls: ['./active-call.component.css']
})
export class ActiveCallComponent implements OnInit, OnDestroy {
  public audioMuted = false;
  public videoMuted = false;
  public callState$: Observable<any>;
  private _stateSubscription: Subscription | null = null;
  public stateData: any = null;

  // Drag logic properties
  public isDragging = false;
  public popupPos = { x: 0, y: 0 };
  private _dragStart = { x: 0, y: 0 };
  private _posStart = { x: 0, y: 0 };
  public hasMoved = false;

  // Bubble Logic Properties
  public phase: CallPhase = 'incoming';
  public menuOpen = false;
  public activeType: CallType | null = null;
  public flipped = false;
  public elapsed = 0;

  public incomingBtns = INCOMING_BTNS;
  public activeBtns = ACTIVE_BTNS;
  public eqBars = EQ_DELAYS.map((delay, i) => ({ delay, height: EQ_HEIGHTS[i] }));
  private autoClose: any = null;
  private ticker: any = null;
  public callType: CallType = 'video';
  public signal = 'Sub-Sonic';

  constructor(
    public agoras: AgoraService,
    public _as: AuthService,
    private _ocs: OutgoingCallService,
    private _rs: RoutingService,
    private _ss: SocketService,
    private _cds: ChatDataService,
    private cdr: ChangeDetectorRef
  ) {
    this.callState$ = this._ocs.callState$.asObservable();
  }

  ngOnInit(): void {
    this.menuOpen = false;
    this._stateSubscription = this.callState$.subscribe((state) => {
      this.stateData = state;
      if (state.status === 'active') {
        this.phase = 'active';
        this.videoMuted = !state.isVideoEnabled;
        this.activeType = state.isVideoEnabled ? 'video' : 'audio';

        setTimeout(() => {
          this.agoras.playLocalStream();
          this.agoras.playRemoteStreams();
          this.startTimer();
          this.cdr.detectChanges();
        }, 800);
      } else if (state.status === 'ringing') {
        this.phase = 'incoming';
        this.videoMuted = !state.isVideoEnabled;
        this.callType = state.isVideoEnabled ? 'video' : 'audio';
      } else if (state.status === 'idle') {
        this.phase = 'declined';
        this.hasMoved = false;
        this.closeMenu();
        this.stopTimer();
      }
      this.cdr.detectChanges();
    });
  }

  ngOnDestroy(): void {
    if (this._stateSubscription) this._stateSubscription.unsubscribe();
    this.closeMenu();
    this.stopTimer();
  }

  /* --- Dragging --- */
  public onDragStart(event: MouseEvent): void {
    // Only start dragging if targeting root or stage directly, not buttons
    if ((event.target as HTMLElement).closest('.cw-abtn') || (event.target as HTMLElement).closest('.cw-bubble')) {
      return;
    }
    this.isDragging = true;
    this.hasMoved = true;
    this._dragStart = { x: event.clientX, y: event.clientY };
    this._posStart = { x: this.popupPos.x, y: this.popupPos.y };
    event.preventDefault();
  }

  @HostListener('document:mousemove', ['$event'])
  public onDragMove(event: MouseEvent): void {
    if (!this.isDragging) return;
    const dx = event.clientX - this._dragStart.x;
    const dy = event.clientY - this._dragStart.y;
    this.popupPos = { x: this._posStart.x + dx, y: this._posStart.y + dy };
  }

  @HostListener('document:mouseup')
  public onDragEnd(): void {
    this.isDragging = false;
  }

  /* --- Data Helpers --- */
  public get inCall(): boolean { return !!(this.agoras.showStopButton || this._ocs.currentlyOnCall); }

  public getRemoteUser(state: any): any {
    if (!state) return null;
    const myId = String(this._as.user?._id || this._as.user?.id || '');
    const fromUser = state.fromUser || null;
    const toUser = state.toUser || null;
    if (myId && fromId(fromUser) === myId) return toUser;
    if (myId && fromId(toUser) === myId) return fromUser;
    return toUser || fromUser || null;
    function fromId(u: any) { return String(u?._id || u?.id || ''); }
  }

  public getProfileImage(user: any): string | null {
    if (!user) return null;
    const profileImage = typeof user?.profileImage === 'string' ? user.profileImage.trim() : '';
    return profileImage ? profileImage : 'assets/images/user-login.svg';
  }

  /* --- Actions --- */
  public hangUp(): void {
    const channelId = this.agoras.channel;
    const userId = this._as.user?._id;
    if (channelId && userId) {
      this._ss.emitLeaveCall(channelId, userId);
    }
    this.agoras.stop();
    this._ocs.currentlyOnCall = false;
    this.closeMenu();
    this.stopTimer();
    this.phase = 'declined';
  }

  public openChat(): void {
    if (this.stateData && this.getRemoteUser(this.stateData)) {
      this._cds.setActiveChat(this.getRemoteUser(this.stateData)._id);
      this._rs.home();
      this.closeMenu();
    }
  }

  public toggleAudio(): void {
    const track = this.agoras.localAudioTrack;
    if (track) {
      track.setMuted(!this.audioMuted);
      this.audioMuted = !this.audioMuted;
    }
  }

  public toggleVideo(): void {
    const track = this.agoras.localVideoTrack;
    if (track) {
      track.setMuted(!this.videoMuted);
      this.videoMuted = !this.videoMuted;
      // Emit state change if desired
    }
  }

  /* --- Bubble UI Logic --- */
  get phaseLabel(): string {
    switch (this.phase) {
      case 'incoming': return this.callType === 'video' ? 'Incoming FaceTime' : 'Incoming Call';
      case 'active': return this.activeType === 'video' ? 'FaceTime Active' : 'Voice Call Active';
      case 'hold': return 'On Hold';
      default: return 'Call Declined';
    }
  }
  get subLabel(): string { return this.phase === 'active' ? (this.activeType === 'video' ? 'Video Connected' : 'Audio Connected') : this.signal; }
  get statusText(): string {
    if (this.phase === 'active') return `Active · ${this.timerDisplay}`;
    if (this.phase === 'hold') return 'Holding…';
    if (this.phase === 'declined') return 'Disconnected';
    return `Incoming · ${this.signal}`;
  }
  get timerDisplay(): string {
    const m = String(Math.floor(this.elapsed / 60)).padStart(2, '0');
    const s = String(this.elapsed % 60).padStart(2, '0');
    return `${m}:${s}`;
  }
  get incomingOpen(): boolean { return this.menuOpen && this.phase === 'incoming'; }
  get activeOpen(): boolean { return this.menuOpen && this.phase === 'active'; }
  get pulseActive(): boolean { return this.phase === 'incoming'; }
  get showEnd(): boolean { return this.phase === 'active'; }
  get showHint(): boolean { return this.phase === 'incoming' || this.phase === 'active'; }
  get hintText(): string { return this.phase === 'active' ? 'Tap to show controls' : 'Tap bubble to answer'; }
  get bubbleGlow(): string {
    switch (this.phase) {
      case 'active': return '0 0 48px rgba(206,189,255,.55),0 8px 40px rgba(0,0,0,.8)';
      case 'hold': return '0 0 28px rgba(68,216,241,.4),0 8px 40px rgba(0,0,0,.8)';
      case 'declined': return 'none';
      default: return '0 0 32px rgba(206,189,255,.4),0 8px 40px rgba(0,0,0,.7)';
    }
  }
  get bubbleTransform(): string {
    if (this.phase === 'declined') return 'scale(0.72)';
    if (this.flipped) return 'scaleX(-1)';
    return 'scale(1)';
  }
  get progressDuration(): string { return this.phase === 'active' ? '4s' : '2.8s'; }

  onBubbleTap(): void {
    if (this.phase !== 'incoming' && this.phase !== 'active') return;
    this.menuOpen ? this.closeMenu() : this.openMenu(this.phase === 'active' ? 4000 : 2800);
  }

  handleIncoming(id: string): void {
    switch (id) {
      case 'in-decline':
        this.hangUp();
        break;
      case 'in-audio':
      case 'in-facetime':
        // Handled by incoming-call.component — do nothing here
        this.closeMenu();
        break;
      case 'in-hold':
        this.phase = 'hold'; this.closeMenu(); break;
      case 'in-myroon':
        break;
    }
  }

  handleActive(id: string): void {
    switch (id) {
      case 'ac-mute': this.toggleAudio(); break;
      case 'ac-speaker': this.toggleVideo(); break;
      case 'ac-flip': this.flipped = !this.flipped; break;
      case 'ac-chat': this.openChat(); break;
      case 'ac-facetime':
        this.agoras.enableVideo();
        this.videoMuted = false;
        this.activeType = 'video';
        this.cdr.markForCheck();
        break;
      case 'ac-myroon': break;
    }
  }

  onEnd(): void { this.hangUp(); }

  getControlIcon(btn: any): string { return (btn.id === 'ac-mute' && this.audioMuted) || (btn.id === 'ac-speaker' && this.videoMuted) ? (btn.iconOff ?? btn.icon) : btn.icon; }
  getControlLabel(btn: any): string { return (btn.id === 'ac-mute' && this.audioMuted) || (btn.id === 'ac-speaker' && this.videoMuted) ? (btn.labelOff ?? btn.label) : btn.label; }
  getControlColor(btn: any): string { return (btn.id === 'ac-mute' && this.audioMuted) || (btn.id === 'ac-speaker' && this.videoMuted) ? btn.colorOn : btn.color; }
  getControlBg(btn: any): string { return (btn.id === 'ac-mute' && this.audioMuted) || (btn.id === 'ac-speaker' && this.videoMuted) ? btn.bgOn : btn.bg; }

  btnTransform(btn: any, visible: boolean): string {
    return visible ? `translate(calc(-50% + ${btn.tx}px), calc(-50% + ${btn.ty}px)) scale(1)` : 'translate(-50%,-50%) scale(0.4)';
  }
  btnTransition(delay: number): string { return `opacity .22s ${delay / 1000}s, transform .4s cubic-bezier(.34,1.56,.64,1) ${delay / 1000}s`; }
  eqAnimation(delay: number): string { return this.phase === 'active' && !this.audioMuted ? `cwEq 1.2s ease-in-out ${delay}s infinite alternate` : 'none'; }

  private startTimer(): void {
    this.elapsed = 0;
    this.ticker = setInterval(() => { this.elapsed++; this.cdr.detectChanges(); }, 1000);
  }
  private stopTimer(): void { if (this.ticker) { clearInterval(this.ticker); this.ticker = null; } }
  private openMenu(ms: number): void {
    this.menuOpen = true;
    clearTimeout(this.autoClose);
    this.autoClose = setTimeout(() => { this.menuOpen = false; this.cdr.detectChanges(); }, ms);
    this.cdr.detectChanges();
  }
  private closeMenu(): void { this.menuOpen = false; clearTimeout(this.autoClose); this.cdr.detectChanges(); }
  trackById(_: number, item: any): string { return item.id; }

}
